import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Component, useEffect, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";
import { DashboardLayout } from "./components/DashboardLayout";
import { FloatingSocialSidebar } from "./components/FloatingSocialSidebar";
import { ConsentBanner } from "./components/ConsentBanner";
import { hasAnalyticsConsent } from "./lib/consent";

// Lazy load all page components for code splitting
const Home = lazy(() => import("./pages/Home"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Calculators = lazy(() => import("./pages/Calculators"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const Docs = lazy(() => import("./pages/Docs"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogManagement = lazy(() => import("./pages/admin/BlogManagement"));
const AdminAnalytics = lazy(() => import("./pages/admin/Analytics"));
const AdminDiscountCodes = lazy(() => import("./pages/admin/DiscountCodes"));
const AdminAgentConsole = lazy(() => import("./pages/admin/AgentConsole"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Overview = lazy(() => import("./pages/dashboard/Overview"));
const Referrals = lazy(() => import("./pages/dashboard/Referrals"));
const Rewards = lazy(() => import("./pages/dashboard/Rewards"));
const Analytics = lazy(() => import("./pages/dashboard/Analytics"));
const Settings = lazy(() => import("./pages/dashboard/Settings"));
const Billing = lazy(() => import("./pages/dashboard/Billing"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Demo = lazy(() => import("./pages/Demo"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const isDynamicImportError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);

  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk \d+ failed/i.test(message);
};

const reloadForFreshAssets = (reason: string) => {
  if (typeof window === "undefined") return false;

  const reloadKey = `refertle:asset-reload:${window.location.pathname}${window.location.search}`;
  if (sessionStorage.getItem(reloadKey) === "done") return false;

  sessionStorage.setItem(reloadKey, "done");
  console.info(`[asset-recovery] Reloading to refresh stale app assets: ${reason}`);
  window.location.reload();
  return true;
};

class LazyRouteErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo);

    if (isDynamicImportError(error)) {
      reloadForFreshAssets("dynamic import failed");
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
        <section className="max-w-md text-center space-y-5">
          <h1 className="text-3xl font-bold tracking-normal">We refreshed the app</h1>
          <p className="text-muted-foreground">
            A newer version is available. Reload once to continue with the latest assets.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Reload page
          </button>
        </section>
      </main>
    );
  }
}

const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Only fire GA4 pageviews when the user has granted analytics consent.
    if (!hasAnalyticsConsent()) return;
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-W3PSP90D4Q', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

const AssetPreloadRecovery = () => {
  useEffect(() => {
    const handlePreloadError = (event: Event) => {
      event.preventDefault();
      reloadForFreshAssets("vite preload failed");
    };

    window.addEventListener("vite:preloadError", handlePreloadError);

    return () => window.removeEventListener("vite:preloadError", handlePreloadError);
  }, []);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AssetPreloadRecovery />
        <RouteTracker />
        <FloatingSocialSidebar />
        <ConsentBanner />
        <LazyRouteErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/calculators" element={<Calculators />} />
              <Route path="/waitlist" element={<Waitlist />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/admin/blog" element={<BlogManagement />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/discount-codes" element={<AdminDiscountCodes />} />
              <Route path="/admin/agent-console" element={<AdminAgentConsole />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/dashboard" element={<DashboardLayout><Overview /></DashboardLayout>} />
              <Route path="/dashboard/referrals" element={<DashboardLayout><Referrals /></DashboardLayout>} />
              <Route path="/dashboard/rewards" element={<DashboardLayout><Rewards /></DashboardLayout>} />
              <Route path="/dashboard/analytics" element={<DashboardLayout><Analytics /></DashboardLayout>} />
              <Route path="/dashboard/settings" element={<DashboardLayout><Settings /></DashboardLayout>} />
              <Route path="/dashboard/billing" element={<DashboardLayout><Billing /></DashboardLayout>} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </LazyRouteErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;